import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { settleExpensePayment } from '../../store/expenseStore/expenseThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { ExpensePayment } from '../../utils/expense.types';
import { Utils } from '../../utils/common-utils';
import { ApiStatus } from '../../utils/api.types';
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';

interface DialogSettleExpenseProps {
  open: boolean;
  expense: ExpensePayment | null;
  onClose: (settled?: boolean) => void;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogSettleExpense: FC<DialogSettleExpenseProps> = ({ open, expense, onClose }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.expense);
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [paymentMode, setPaymentMode] = useState('CASH');
  const [isCash, setIsCash] = useState(true);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);
  const [instrumentId, setInstrumentId] = useState('');
  const [remarks, setRemarks] = useState('');

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });
  const [tdsAmount, setTdsAmount] = useState<number>(0);
  const [netPayableAmount, setNetPayableAmount] = useState<number>(0);
  const [applyTds, setApplyTds] = useState<boolean>(false);
  const [expenseAmount, setExpenseAmount] = useState<number>(0);

  // Load TDS rates once
  useEffect(() => {
    let isMounted = true;
    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    if (isMounted) {
      getTdsRates();
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  // Load banks when selectedAccount changes
  useEffect(() => {
    const getBankDetails = async () => {
      if (selectedAccount?._id) {
        const result: any = await dispatch(fetchBanks(selectedAccount._id));
        if (result.payload?.banks?.length > 0) {
          const bankOptions = result.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          // Set first bank as default
          const firstBank = result.payload.banks[0];
          setSelectedFromBankAccount(firstBank._id);
        }
      }
    };

    getBankDetails();
  }, [selectedAccount, dispatch]);

  // Reset form and set expense data when expense changes
  useEffect(() => {
    if (open && expense) {
      // Reset form
      setPaymentMode('CASH');
      setIsCash(true);
      setPaymentDate(new Date());
      setSelectedFromBankAccount('');
      setInstrumentId('');
      setRemarks('');

      // Set expense amount
      const amount = parseFloat(expense.amount as any) || 0;
      setExpenseAmount(amount);

      // Check if TDS was already applied when expense was booked
      if (expense.tdsSelected && expense.tdsSelected.apply) {
        setApplyTds(true);
        setTdsSelected({
          _id: expense.tdsSelected.tdsSelected?._id || "",
          label: expense.tdsSelected.tdsSelected?.label || "",
          rate: expense.tdsSelected.tdsSelected?.rate || 0
        });
        const existingTds = parseFloat(expense.tdsSelected.totalAmount as any) || 0;
        setTdsAmount(existingTds);
        setNetPayableAmount(amount - existingTds);
      } else {
        // Reset TDS fields
        setApplyTds(false);
        setTdsSelected({ _id: "", label: "", rate: 0 });
        setTdsAmount(0);
        setNetPayableAmount(amount);
      }
    }
  }, [open, expense]);

  // Handle payment mode changes
  useEffect(() => {
    if (paymentMode === 'CASH') {
      setIsCash(true);
      setSelectedFromBankAccount('');
    } else {
      setIsCash(false);
    }
  }, [paymentMode]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMode(event.target.value);
  };

  const handleDateChange = (newValue: Date | null) => {
    if (newValue) {
      setPaymentDate(newValue);
    }
  };

  // Calculate TDS amount
  const calculateTds = (amount: number, tdsRate: number) => {
    if (!amount || !tdsRate) return 0;
    return Number((tdsRate * amount) / 100);
  };

  // Handle TDS selection change
  const handleTdsChange = (event: SelectChangeEvent<any>) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      // Clear TDS
      setApplyTds(false);
      setTdsSelected({ _id: "", label: "", rate: 0 });
      setTdsAmount(0);
      setNetPayableAmount(expenseAmount);
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption && selectedTdsOption.rate) {
      setApplyTds(true);
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate
      });

      // Calculate TDS amount
      const calculatedTds = calculateTds(expenseAmount, selectedTdsOption.rate);
      setTdsAmount(calculatedTds);
      setNetPayableAmount(expenseAmount - calculatedTds);
    }
  };

  const handleSubmit = async () => {
    if (!expense) return;

    try {
      const settlementPayload: any = {
        expenseId: expense._id,
        paymentMode,
        paymentDate: paymentDate,
        fromBankAccount: paymentMode === 'CASH' ? undefined : selectedFromBankAccount,
        instrumentNumber: instrumentId || undefined,
        remarks: remarks || undefined,
        referenceNumber: `SETTLE-${expense.txnId}`,
      };

      // Add TDS data if applied
      if (applyTds && tdsAmount > 0) {
        settlementPayload.tdsSelected = {
          apply: true,
          totalAmount: tdsAmount.toFixed(2),
          taxableAmount: expenseAmount,
          displayType: 0,
          tdsSelected: {
            _id: tdsSelected._id,
            label: tdsSelected.label,
            rate: tdsSelected.rate
          }
        };
        settlementPayload.tdsAmount = tdsAmount;
      }

      await dispatch(settleExpensePayment(settlementPayload)).unwrap();

      onClose(true);
    } catch (error) {
      console.error('Failed to settle expense:', error);
    }
  };

  const handleClose = () => {
    onClose(false);
  };

  if (!expense) return null;

  return (
    <div className="drawer-form-container" style={{width: "100%", maxWidth: "800px"}}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: "2px 0 0 2px",
          position: "relative"
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Settle Expense Payment
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{
          padding: '16px',
          paddingBottom: '100px',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          backgroundColor: '#fafafa',
          flex: 1
        }}>
          <Grid container spacing={2} style={{paddingTop: '20px'}}>

            {/* Expense Details Section */}
            <Grid item xs={12}>
              <Box sx={{
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e5e7eb',
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      {expense.paymentFor === 'account' ? 'Account' : expense.paymentFor === 'employee' ? 'Employee' : 'Vendor'}
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {expense.paymentFor === 'account'
                        ? ((expense as any)._partyName || selectedAccount?.legalName || selectedAccount?.name || 'Self Account')
                        : expense.employee?.name || expense.paymentTo?.legalName || expense.paymentTo?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Amount
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="primary">
                      {Utils.formatNumber(parseFloat(expense.amount as any) || 0, true, true)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Expense Date
                    </Typography>
                    <Typography variant="body2">
                      {moment(expense.paymentDate).format('DD MMM YYYY')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      TXN ID
                    </Typography>
                    <Typography variant="body2">
                      #{expense.txnId}
                    </Typography>
                  </Grid>

                  {/* Show existing TDS if already applied */}
                  {expense.tdsSelected && expense.tdsSelected.apply && (
                    <>
                      <Grid item xs={12}>
                        <Box sx={{
                          p: 1.5,
                          backgroundColor: '#fff7ed',
                          borderRadius: '4px',
                          border: '1px solid #fed7aa'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            ℹ️ TDS Already Applied at Booking
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {expense.tdsSelected.tdsSelected?.label} - {Utils.formatNumber(parseFloat(expense.tdsSelected.totalAmount as any) || 0, true, true)}
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>

            {/* TDS Section - Allow applying/updating TDS during settlement */}
            <Grid item xs={12}>
              <Box sx={{
                borderRadius: '8px',
                p: 2,
                border: '1px solid #e5e7eb',
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, color: '#374151', fontWeight: 600 }}>
                  TDS Deduction {expense.tdsSelected?.apply ? '(Update if needed)' : '(Apply if missed)'}
                </Typography>
                <Grid container spacing={2}>
                  {/* TDS Selection */}
                  <Grid item xs={12}>
                    <CustomSelect
                      name="tdsSelected"
                      label="Apply TDS (Optional)"
                      id="tdsSelected"
                      size="small"
                      options={[{ label: 'No TDS', value: '' }, ...tdsTaxRateOptions]}
                      value={tdsSelected._id || ""}
                      onChange={handleTdsChange}
                      sx={{
                        '& .MuiInputLabel-root': {
                          transform: 'translate(14px, 9px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.75)'
                          }
                        },
                        '& .MuiSelect-select': {
                          padding: '8.5px 14px',
                        }
                      }}
                    />
                  </Grid>

                  {/* TDS Amount Display */}
                  <Grid item xs={12}>
                    {applyTds && tdsAmount > 0 && (
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: '#f0fdf4',
                          border: '1px solid #86efac',
                          borderRadius: '6px',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#16a34a',
                            fontWeight: 500,
                            mb: 0.5
                          }}
                        >
                          TDS Deducted ({tdsSelected.rate}%)
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            color: '#15803d',
                            fontWeight: 600
                          }}
                        >
                          {Utils.formatNumber(tdsAmount.toFixed(2))}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: '#16a34a',
                            mt: 0.5
                          }}
                        >
                          Net Payable: {Utils.formatNumber(netPayableAmount.toFixed(2))}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Payment Method Section */}
            <Grid item xs={12}>
              <Box sx={{
                backgroundColor: '#f3f3f3',
                borderRadius: '8px',
                p: 2,
                pt:2,
                border: '1px solid #e5e7eb',
              }}>
                <Grid container spacing={2} style={{paddingTop: '10px'}}>

                  {/* From Account */}
                  <Grid item xs={12} md={6}>
                    <CustomSelect
                      sx={{
                        width: '100%',
                        '& .MuiInputLabel-root': {
                          transform: 'translate(14px, 9px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -9px) scale(0.75)'
                          }
                        },
                        '& .MuiSelect-select': {
                          padding: '8.5px 14px',
                        }
                      }}
                      name="fromBankAccount"
                      label="From Account"
                      id="fromBankAccount"
                      variant="outlined"
                      size='small'
                      disabled={isCash}
                      options={fromBankAccountOptions}
                      value={selectedFromBankAccount ?? ""}
                      onChange={(event: SelectChangeEvent<any>) => {
                        setSelectedFromBankAccount(event.target.value);
                      }}
                    />
                  </Grid>

                  {/* Payment Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        label="Payment Date *"
                        inputFormat="dd/MM/yyyy"
                        value={paymentDate}
                        onChange={handleDateChange}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="paymentDate"
                            required
                            variant="outlined"
                            size='small'
                            fullWidth
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </Grid>

                  {/* Payment Mode Radio Group */}
                  <Grid item xs={12}>
                    <RadioGroup
                      row
                      aria-labelledby="payment-mode-label"
                      value={paymentMode}
                      name="paymentMode"
                      onChange={handleRadioChange}
                      sx={{
                        '& .MuiFormControlLabel-root': {
                          mr: 2,
                          ml: 0,
                          '& .MuiRadio-root': {
                            p: 0.5,
                            mr: 0.5,
                            color: '#6366f1',
                            '&.Mui-checked': {
                              color: '#6366f1',
                            }
                          },
                          '& .MuiTypography-root': {
                            fontSize: '0.875rem'
                          }
                        }
                      }}
                    >
                      <FormControlLabel value="CASH" control={<Radio size="small" />} label="Cash" />
                      <FormControlLabel value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                      <FormControlLabel value="CARD" control={<Radio size="small" />} label="Card" />
                      <FormControlLabel value="UPI" control={<Radio size="small" />} label="UPI" />
                      <FormControlLabel value="NETBANKING" control={<Radio size="small" />} label="Net Banking" />
                    </RadioGroup>
                  </Grid>

                  {/* Instrument ID */}
                  <Grid item xs={12} md={6}>
                    <TextField
                      disabled={isCash}
                      variant="outlined"
                      size='small'
                      fullWidth
                      label={paymentMode === 'CHEQUE' ? "Cheque Number" :
                             paymentMode === 'UPI' ? "UPI ID" :
                             paymentMode === 'CARD' ? "Card Ref" :
                             "Transaction Ref"}
                      name="instrumentId"
                      value={instrumentId}
                      onChange={(e) => setInstrumentId(e.target.value)}
                    />
                  </Grid>

                  {/* Empty space for alignment */}
                  <Grid item xs={12} md={6} />

                  {/* Remarks */}
                  <Grid item xs={12}>
                    <TextField
                      label="Remarks"
                      name="remarks"
                      variant="outlined"
                      size='small'
                      fullWidth
                      multiline
                      rows={2}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#fff',
          borderTop: '1px solid #c1c5cb',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          padding: '16px 24px',
          margin: 0,
          zIndex: 1000
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            width: '100%'
          }}>
            <Button
              variant='outlined'
              onClick={handleClose}
              sx={{
                minWidth: '100px',
                height: '40px'
              }}
            >
              Cancel
            </Button>

            <LoadingButton
              type="button"
              onClick={handleSubmit}
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{
                minWidth: '140px',
                height: '40px'
              }}
            >
              Settle Payment
            </LoadingButton>
          </Box>
        </DialogActions>
      </Box>
    </div>
  );
};

export default DialogSettleExpense;
